import Switch from '@mui/joy/Switch';
import Typography from '@mui/joy/Typography';
import Box from '@mui/joy/Box';

/**
 * options.stt.aux → toggle state. Absent means ON — the default for agents
 * on models that accept the option — and only an explicit `enabled: false`
 * (or `false`) means off. Shared by the main agent page and the set editor so
 * both read a stored agent the same way.
 */
export const auxSttFromOptions = (options) => {
  const aux = options?.stt?.aux;
  if (aux === undefined) return true;
  return !!aux && aux.enabled !== false;
};

/**
 * options.tts.output → toggle state. OFF unless the agent already carries the
 * block (it adds cost, so it is never assumed): `true` or an object without
 * `enabled: false` reads as on.
 */
export const outputSttFromOptions = (options) => {
  const output = options?.tts?.output;
  return output === true || (!!output && typeof output === 'object' && output.enabled !== false);
};

/**
 * Apply one side-STT toggle to a block of options: on writes a Deepgram engine
 * unless a stored block already tunes vendor/language (kept; only a stored
 * `enabled: false` is dropped), off removes the block (the API reads absent as
 * off). The parent block is dropped altogether when it ends up empty.
 */
const withSideStt = (options, parentKey, childKey, on) => {
  const parent = { ...(options?.[parentKey] || {}) };
  if (on) {
    const tuned = parent[childKey] && typeof parent[childKey] === 'object' ? { ...parent[childKey] } : {};
    delete tuned.enabled;
    parent[childKey] = { ...tuned, vendor: tuned.vendor || 'deepgram' };
  } else {
    delete parent[childKey];
  }
  const out = { ...(options || {}) };
  if (Object.keys(parent).length) out[parentKey] = parent;
  else delete out[parentKey];
  return out;
};

/** The caller-side toggle applied to options (models with hasAuxStt only). */
export const withAuxStt = (options, auxStt) => withSideStt(options, 'stt', 'aux', auxStt);

/** The agent-side toggle applied to options (models with hasOutputStt only). */
export const withOutputStt = (options, outputStt) => withSideStt(options, 'tts', 'output', outputStt);

/**
 * "Auxiliary transcription" switches — two independent Deepgram recognitions,
 * each rendered only for models the catalogue marks capable:
 *  - the caller (`hasAuxStt`, options.stt.aux → `user-aux` bubbles, metered as
 *    auxiliary STT), on by default;
 *  - the agent's own audio (`hasOutputStt`, options.tts.output → `agent-speech`
 *    bubbles, metered as output audit STT), off by default — it adds cost.
 */
export default function AuxSttToggle({
  hasAuxStt,
  auxStt,
  setAuxStt,
  hasOutputStt,
  outputStt,
  setOutputStt,
  disabled,
  sx,
}) {
  if (!hasAuxStt && !hasOutputStt) return null;
  return (
    <Box sx={{ m: 2, ...(sx || {}) }}>
      {hasAuxStt && (
        <Box sx={{ mb: hasOutputStt ? 1.5 : 0 }}>
          <Switch
            checked={!!auxStt}
            disabled={disabled}
            onChange={(e) => setAuxStt && setAuxStt(e.target.checked)}
            endDecorator={<Typography level="body-sm">Auxiliary transcription (Deepgram second opinion)</Typography>}
          />
          <Typography level="body-xs" sx={{ mt: 0.5, opacity: 0.8 }}>
            Runs a second Deepgram recognition of the caller next to the agent's own; each caller line gets a
            second-opinion bubble in the transcript, metered separately as auxiliary STT.
          </Typography>
        </Box>
      )}
      {hasOutputStt && (
        <Box>
          <Switch
            checked={!!outputStt}
            disabled={disabled}
            onChange={(e) => setOutputStt && setOutputStt(e.target.checked)}
            endDecorator={<Typography level="body-sm">Transcribe agent audio</Typography>}
          />
          <Typography level="body-xs" sx={{ mt: 0.5, opacity: 0.8 }}>
            Produces transcription of the audio the agent outputs. Useful for debugging things like pronunciation and
            how the agent outputs textual constructs like dates and times of day. Adds unnecessary cost unless needed.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
